"use client"
import { useEffect, useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Loader2, RefreshCw, UsersRound } from "lucide-react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import Pagination from "../../Common/Pagination"

const fullName = (user) => {
  if (!user) return "Unknown"
  const name =
    user.name || [user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ").trim()
  return name || "Unknown"
}

const normalizeTeachers = (payload) => {
  if (Array.isArray(payload?.data?.teachers)) return payload.data.teachers
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.teachers)) return payload.teachers
  return []
}

const normalizeEnrollments = (payload) => {
  if (Array.isArray(payload?.data?.enrollments)) return payload.data.enrollments
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

const asId = (value) => {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object" && value._id) return asId(value._id)
  if (typeof value.toString === "function") return value.toString()
  return String(value)
}

export default function TeacherBiometricMapping() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [search, setSearch] = useState("")
  const [teachers, setTeachers] = useState([])
  const [enrollments, setEnrollments] = useState([])
  const [totalTeachers, setTotalTeachers] = useState(0)
  const [mappedTotal, setMappedTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError("")

      const teachersRes = await axiosInstance.get(API_ENDPOINTS.usersTeachers, {
        params: {
          paginate: true,
          page: currentPage,
          limit: itemsPerPage,
          status: "all",
          search: search.trim() ? search.trim() : undefined,
        },
      })

      const teacherList = normalizeTeachers(teachersRes.data)
      setTeachers(teacherList)
      setTotalTeachers(Number(teachersRes.data?.data?.count || 0))

      const teacherIds = teacherList.map((t) => asId(t?._id)).filter(Boolean)
      const [enrollmentsRes, mappedRes] = await Promise.all([
        teacherIds.length
          ? axiosInstance.get(API_ENDPOINTS.biometricEnrollments, {
              params: { role: "teacher", userIds: teacherIds.join(",") },
            })
          : Promise.resolve({ data: { data: { enrollments: [] } } }),
        axiosInstance.get(API_ENDPOINTS.biometricEnrollments, {
          params: { role: "teacher", active: "true", paginate: true, page: 1, limit: 1 },
        }),
      ])

      setEnrollments(normalizeEnrollments(enrollmentsRes.data))
      setMappedTotal(Number(mappedRes.data?.total ?? mappedRes.data?.data?.count ?? 0))
    } catch (err) {
      console.error("Error loading biometric mapping:", err)
      setError(err.response?.data?.message || "Failed to load teacher biometric mapping")
      setTeachers([])
      setEnrollments([])
      setTotalTeachers(0)
      setMappedTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [currentPage, itemsPerPage, search])

  const rows = useMemo(() => {
    const enrollmentByUserId = new Map(
      enrollments
        .filter((e) => e?.user)
        .map((e) => [
          String(asId(e.user)),
          e,
        ])
    )

    return teachers.map((teacher) => {
      const userId = String(asId(teacher._id))
      const enrollment = enrollmentByUserId.get(userId)
      return {
        userId,
        teacherName: fullName(teacher),
        employeeCode: teacher.employeeCode || teacher.staffId || "-",
        mapped: Boolean(enrollment),
        deviceUserId: enrollment?.deviceUserId || "-",
        deviceSn: enrollment?.deviceSn || "-",
        isActive: enrollment?.isActive ?? false,
        lastSyncedAt: enrollment?.lastSyncedAt || null,
      }
    })
  }, [teachers, enrollments])

  useEffect(() => {
    setCurrentPage(1)
  }, [search])
  const mappedCountOnPage = rows.filter((r) => r.mapped && r.isActive).length

  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              <UsersRound className="h-5 w-5 text-[#0263cc]" />
              Teacher Biometric Mapping
            </CardTitle>
            <CardDescription>
              View which teachers are mapped to biometric attendance and device IDs.
            </CardDescription>
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-between gap-3">
          <Input
            placeholder="Search by name, employee code, device user ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />
          <Badge variant="secondary">
            Mapped: {mappedTotal}/{totalTeachers} (this page: {mappedCountOnPage})
          </Badge>
        </div>

        <div className="overflow-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-700">Teacher</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700">Employee Code</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700">Mapped</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700">Device User ID</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700">Device SN</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700">Last Sync</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading mapping...
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No teachers found for current filter.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.userId} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-900">{row.teacherName}</td>
                    <td className="px-4 py-3 text-gray-700">{row.employeeCode}</td>
                    <td className="px-4 py-3">
                      {row.mapped && row.isActive ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Mapped</Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-600">Not mapped</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.deviceUserId}</td>
                    <td className="px-4 py-3 text-gray-700">{row.deviceSn}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.lastSyncedAt ? new Date(row.lastSyncedAt).toLocaleString() : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={totalTeachers}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          currentPageCount={rows.length}
          itemLabel="teachers"
        />
      </CardContent>
    </Card>
  )
}
